// Libraries
import { useState, useEffect, useRef, FormEvent } from 'react';
import { useMediaQuery } from 'react-responsive';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMessageArrowUp,
    faMessageArrowDown,
} from '@fortawesome/pro-solid-svg-icons';
import { faFaceSmile, faPaperPlane } from '@fortawesome/pro-light-svg-icons';

// Hooks
import { useAppSelector, useAppDispatch } from '../../../app/hooks';

// Models
import { Message } from '../../../features/chat/chatModels';
import { OtherUser } from '../../../features/user/userModels';

// Features
import { selectSession } from '../../../features/session/sessionSlice';
import {
    selectUser,
    putUserAsync,
    // postAbusiveUserAsync,
} from '../../../features/user/userSlice';
import {
    selectMessages,
    selectMessagesStatus,
    selectEmojiListStatus,
    postMessageAsync,
    pushMessagesStatus,
    pushEmojiListStatus,
} from '../../../features/chat/chatSlice';

// Components
import ChatMessages from '../../molecules/ChatMessages/ChatMessages';
import EmojiList from '../../molecules/EmojiList/EmojiList';
import ChatMessage from '../../atoms/ChatMessage/ChatMessage';
import IconButton from '../../atoms/IconButton/IconButton';

// Utilities
import {
    mediaLandscapeMobile,
    mediaPortrait,
} from '../../../utils/misc/mediaQueries';

// Styles
import styles from './Chat.module.css';

/**
 * @function Chat
 * Interactive messaging between players
 *
 * @example
 * return (
 *   <Chat />
 * )
 */
function Chat(): JSX.Element {
    const dispatch = useAppDispatch();
    const currentUser = useAppSelector(selectUser);
    const messages = useAppSelector(selectMessages);
    const messagesOpen = useAppSelector(selectMessagesStatus);
    const emojiListOpen = useAppSelector(selectEmojiListStatus);
    const session = useAppSelector(selectSession);
    const user = useAppSelector(selectUser);
    const isPortrait = useMediaQuery(mediaPortrait);
    const isLandscapeMobile = useMediaQuery(mediaLandscapeMobile);

    const [targetUser, setTargetUser] = useState<OtherUser | null>(null);
    const [messageInput, setMessageInput] = useState('');
    const messagesEndRef = useRef<null | HTMLDivElement>(null);

    if (session === null || session === undefined) return <></>;

    // useEffect(() => {
    //   // Alert if clicked on outside of element
    //   function handleClickOutside(event: any) {
    //     if (ref.current && !ref.current.contains(event.target)) {
    //       handleCloseClick(event);
    //     }
    //   }
    //   // Bind the event listener
    //   document.addEventListener('mousedown', handleClickOutside);
    //   return () => {
    //     // Unbind the event listener on clean up
    //     document.removeEventListener('mousedown', handleClickOutside);
    //   };
    // }, [ref]);

    useEffect(() => {
        setTimeout(() => {
            scrollToBottom();
        }, 500);
    }, [messages, messagesOpen]);

    function scrollToBottom() {
        {
            messagesEndRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
            });
        }
    }

    function handleSubmit(event: FormEvent) {
        if (session !== null && session !== undefined) {
            dispatch(
                postMessageAsync({
                    sessionId: session.id,
                    messageBody: messageInput,
                }),
            );
            dispatch(pushEmojiListStatus(false));
            setMessageInput('');
        }
        event.preventDefault();
    }

    function handleMessagesToggleClick() {
        dispatch(pushMessagesStatus(!messagesOpen));
    }

    function handleEmojiListToggleClick() {
        dispatch(pushEmojiListStatus(!emojiListOpen));
    }

    function handleEmojiClick(emoji: string) {
        setMessageInput((messageInput) => `${messageInput}${emoji}`);
    }

    function handleAvatarClick(user: OtherUser) {
        setTargetUser(user);
    }

    function handleCloseClick() {
        setTargetUser(null);
    }

    function handleBlockClick() {
        if (targetUser !== null) {
            const targetUserString = `/api/v2/users/${targetUser.id}`;
            const newBlockList = user.blocked.includes(targetUserString)
                ? user.blocked.filter((item) => item !== targetUserString)
                : [targetUserString, ...user.blocked];
            dispatch(
                putUserAsync({
                    user: { ...user, blocked: newBlockList },
                }),
            );
            setTargetUser(null);
        }
    }

    function handleAbuseClick() {
        if (targetUser !== null) {
            // dispatch(
            //   postAbusiveUserAsync({
            //     userId: currentUser.id,
            //     blockedUserId: targetUser.id,
            //   })
            // );
            setTargetUser(null);
        }
    }

    return (
        <aside className={styles.container}>
            {emojiListOpen && (
                <EmojiList
                    onClick={(e) =>
                        handleEmojiClick((e.target as HTMLButtonElement).value)
                    }
                />
            )}
            {(isPortrait || isLandscapeMobile) && (
                <IconButton
                    type="button"
                    className="chat-toggle"
                    onClick={() => handleMessagesToggleClick()}
                >
                    <FontAwesomeIcon
                        icon={
                            messagesOpen ? faMessageArrowDown : faMessageArrowUp
                        }
                    />
                </IconButton>
            )}
            {((!isPortrait && !isLandscapeMobile) || messagesOpen === true) && (
                <div
                    className={`${styles.chat}${
                        targetUser !== null ? ` ${styles.disabled}` : ``
                    }`}
                >
                    <ChatMessages>
                        {messages.map(
                            ({ id, text, user }: Message, index: number) => {
                                const type =
                                    user.id === 0
                                        ? 'server'
                                        : user.id === currentUser.id
                                        ? 'client'
                                        : 'user';
                                const lastUserId =
                                    index > 0
                                        ? messages[index - 1].user !== null
                                            ? messages[index - 1].user.id
                                            : -1
                                        : -1;

                                return (
                                    currentUser.blocked.filter(
                                        (id) =>
                                            id === `/api/v2/users/${user.id}`,
                                    ).length === 0 && (
                                        <ChatMessage
                                            key={id}
                                            type={type}
                                            user={user}
                                            showName={!(lastUserId === user.id)}
                                            avatar={user.avatar}
                                            text={text}
                                            onClick={
                                                type === 'user'
                                                    ? () =>
                                                          handleAvatarClick(
                                                              user,
                                                          )
                                                    : () => false
                                            }
                                        />
                                    )
                                );
                            },
                        )}
                        <div className={styles.ref} ref={messagesEndRef} />
                    </ChatMessages>
                    {targetUser !== null && (
                        <div className={styles.menu}>
                            <p>
                                You will not see messages from blocked contacts
                            </p>
                            <button
                                type="button"
                                className={styles.block}
                                onClick={() => handleBlockClick()}
                            >
                                Block {targetUser.username}
                            </button>
                            {/*
              <button
                type="button"
                className={styles.report}
                onClick={() => handleAbuseClick()}
              >
                Report {targetUser.username}
              </button>
              */}
                            <button
                                type="button"
                                onClick={() => handleCloseClick()}
                            >
                                Close
                            </button>
                        </div>
                    )}
                </div>
            )}
            <form
                className={styles.form}
                onSubmit={(e: FormEvent) => handleSubmit(e)}
            >
                <IconButton
                    type="button"
                    onClick={() => handleEmojiListToggleClick()}
                >
                    <FontAwesomeIcon icon={faFaceSmile} />
                </IconButton>
                <input
                    className={styles.input}
                    aria-label="Set message text"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    placeholder="Write your message..."
                />
                <IconButton type="submit" onClick={() => false}>
                    <FontAwesomeIcon icon={faPaperPlane} />
                </IconButton>
            </form>
        </aside>
    );
}

// Exports
export default Chat;
